/**
 * ZezamiiIcon — single registry for all Zezamii platform icons.
 * Wraps Lucide React for standard icons + inline SVG for custom hardware icons.
 *
 * Usage:
 *   import { ZezamiiIcon } from 'zComponents/atoms';
 *   <ZezamiiIcon name='boom-gate' size={24} color='#14B8A6' strokeWidth={1.5} />
 *
 * Registry: custom SVGs for hardware-specific glyphs (dome camera, digital lock, boom gate, …),
 * everything else from Lucide React.
 *
 * ⚠️ NO COUNTS HERE ON PURPOSE. This header carried "54 icons total / 43 Lucide" while the registry
 * actually held 80 keys, and each PR faithfully incremented a number that had been wrong for months.
 * `Object.keys(ICON_REGISTRY).length` is the answer; a hand-maintained tally that no build step checks
 * is only ever a claim. The custom-SVG NAMES below are kept because those a reader cannot derive.
 *
 * Custom SVG names:
 *   'camera' | 'dome-camera' | 'bullet-camera' | 'wiegand-reader' | 'access-panel'
 *   'digital-lock' | 'gateway' | 'boom-gate' | 'locker-unit' | 'garage'
 *   'turnstile' | 'lift' | 'product-lockers' (alias → LockerUnit)
 */

import React from 'react';
import {
	DoorOpen,
	DoorClosed,
	Lock,
	LockOpen,
	ScanLine,
	CreditCard,
	PhoneCall,
	Radio,
	Wifi,
	WifiOff,
	Building2,
	Layers,
	BoxSelect,
	LayoutDashboard,
	LayoutGrid,
	Map,
	MapPin,
	User,
	Users,
	UserPlus,
	UserCheck,
	UserX,
	KeyRound,
	Smartphone,
	Fingerprint,
	Hash,
	ScanFace,
	QrCode,
	ShieldCheck,
	ShieldX,
	Hotel,
	Eye,
	Ticket,
	BrainCircuit,
	CheckCircle2,
	XCircle,
	Clock,
	Calendar,
	AlertTriangle,
	Activity,
	Settings,
	Bell,
	Battery,
	BatteryLow,
	BatteryMedium,
	BatteryFull,
	BatteryWarning,
	Plug,
	Zap,
	Infinity as InfinityIcon,
	Globe,
	Wrench,
	Code,
	Trash2,
	ScrollText,
	NotebookPen,
	FileChartColumn,
	FileClock,
	UserRoundPlus,
	Package,
	Image as ImageIcon,
	ImagePlus,
	RefreshCw,
	ChevronLeft,
	ChevronRight,
	Pencil
} from 'lucide-react';

// ─── Custom SVG helper ───────────────────────────────────────────────────────

function svgProps(size, color, strokeWidth, className, style) {
	return {
		width: size,
		height: size,
		viewBox: '0 0 24 24',
		fill: 'none',
		stroke: color,
		strokeWidth,
		strokeLinecap: 'round',
		strokeLinejoin: 'round',
		className,
		style
	};
}

// ─── Custom SVG: Boom Gate ───────────────────────────────────────────────────

function BoomGate({ size = 24, color = 'currentColor', strokeWidth = 1.5, className, style }) {
	return (
		<svg {...svgProps(size, color, strokeWidth, className, style)}>
			<g transform='translate(2 5.25)'>
				<path d='M 1.5 1.5 C 1.5 0.672 2.172 0 3 0 C 3.828 0 4.5 0.672 4.5 1.5 C 4.5 2.328 3.828 3 3 3 C 2.172 3 1.5 2.328 1.5 1.5 Z M 0 13.5 L 7 13.5' />
				<path d='M 3 13.5 L 3 2.5 M 3 1.5 L 20 1.5 M 7.5 0 L 9.5 3 M 12 0 L 14 3 M 16.5 0 L 18.5 3' />
			</g>
		</svg>
	);
}

// ─── Custom SVG: Locker Unit ─────────────────────────────────────────────────

function LockerUnit({ size = 24, color = 'currentColor', strokeWidth = 1.5, className, style }) {
	return (
		<svg {...svgProps(size, color, strokeWidth, className, style)}>
			<g transform='translate(3 2)'>
				<path d='M 2 0 L 16 0 C 17.333 0 18 0.667 18 2 L 18 18 C 18 19.333 17.333 20 16 20 L 2 20 C 0.667 20 0 19.333 0 18 L 0 2 C 0 0.667 0.667 0 2 0 Z M 10.5 15 L 13 15' />
				<path d='M 9 0 L 9 20 M 0 10 L 18 10 M 5 5 L 7.5 5 M 10.5 5 L 13 5 M 5 15 L 7.5 15' />
			</g>
		</svg>
	);
}

// ─── Custom SVG: Garage Door ─────────────────────────────────────────────────

function GarageDoor({ size = 24, color = 'currentColor', strokeWidth = 1.5, className, style }) {
	return (
		<svg {...svgProps(size, color, strokeWidth, className, style)}>
			<g transform='translate(2 2.5)'>
				<path d='M 2 2 L 18 2 C 18.667 2 19 2.333 19 3 L 19 18 C 19 18.667 18.667 19 18 19 L 2 19 C 1.333 19 1 18.667 1 18 L 1 3 C 1 2.333 1.333 2 2 2 Z M 7.5 16.5 L 12.5 16.5' />
				<path d='M 1 0 L 1 2 M 19 0 L 19 2 M 0 0 L 20 0 M 1 7.7 L 19 7.7 M 1 13.3 L 19 13.3' />
			</g>
		</svg>
	);
}

// ─── Custom SVG: Turnstile ───────────────────────────────────────────────────

function TurnstileIcon({ size = 24, color = 'currentColor', strokeWidth = 1.5, className, style }) {
	return (
		<svg {...svgProps(size, color, strokeWidth, className, style)}>
			<g transform='translate(3 3)'>
				<path
					strokeDasharray='3.5 2'
					d='M 0 9 C 0 4.029 4.029 0 9 0 C 13.971 0 18 4.029 18 9 C 18 13.971 13.971 18 9 18 C 4.029 18 0 13.971 0 9 Z'
				/>
			</g>
			<g transform='translate(5.5 4)'>
				<path d='M 4.5 8 C 4.5 6.895 5.395 6 6.5 6 C 7.605 6 8.5 6.895 8.5 8 C 8.5 9.105 7.605 10 6.5 10 C 5.395 10 4.5 9.105 4.5 8 Z M 7.8 9 L 13 15.4 M 6.5 6 L 6.5 0 M 5.2 9 L 0 15.4' />
			</g>
		</svg>
	);
}

// ─── Custom SVG: Lift / Elevator ─────────────────────────────────────────────

function LiftElevator({ size = 24, color = 'currentColor', strokeWidth = 1.5, className, style }) {
	return (
		<svg {...svgProps(size, color, strokeWidth, className, style)}>
			<g transform='translate(4 3)'>
				<path d='M 2 0 L 14 0 C 15.333 0 16 0.667 16 2 L 16 16 C 16 17.333 15.333 18 14 18 L 2 18 C 0.667 18 0 17.333 0 16 L 0 2 C 0 0.667 0.667 0 2 0 Z M 10 10 L 12 12 L 14 10' />
				<path d='M 8 5 L 8 18 M 4 12 L 4 6 M 4 6 L 2 8 M 4 6 L 6 8 M 12 6 L 12 12' />
			</g>
		</svg>
	);
}

// ─── Custom SVG: Bullet Camera (IP/CCTV bullet cam) ─────────────────────────
// ONE combined outline: large-rx rounded-rect body + right-pointing lens

function BulletCamera({ size = 24, color = 'currentColor', strokeWidth = 1.5, className, style }) {
	return (
		<svg {...svgProps(size, color, strokeWidth, className, style)}>
			<path
				d={
					'M 4.5 4 L 12.5 4 Q 16 4 16 7.5 ' +
					'L 16 9 L 22 12 L 16 15 ' +
					'L 16 16.5 Q 16 20 12.5 20 ' +
					'L 4.5 20 Q 1 20 1 16.5 L 1 7.5 Q 1 4 4.5 4 Z'
				}
			/>
		</svg>
	);
}

// ─── Custom SVG: Dome Camera (CCTV) ─────────────────────────────────────────
// Plate + dome as one outline · dual concentric lens rings

function DomeCamera({ size = 24, color = 'currentColor', strokeWidth = 1.5, className, style }) {
	return (
		<svg {...svgProps(size, color, strokeWidth, className, style)}>
			<path d='M 4 2.5 L 20 2.5 Q 22 2.5 22 4.5 L 22 7 C 22 14.179 17.523 20 12 20 C 6.477 20 2 14.179 2 7 L 2 4.5 Q 2 2.5 4 2.5 Z' />
			<circle cx='12' cy='13.5' r='4.2' />
			<circle cx='12' cy='13.5' r='1.8' />
		</svg>
	);
}

// ─── Custom SVG: Wiegand Reader (proximity card reader) ──────────────────────
// Slim portrait panel · status LED · 3 proximity scan lines

function WiegandReader({ size = 24, color = 'currentColor', strokeWidth = 1.5, className, style }) {
	return (
		<svg {...svgProps(size, color, strokeWidth, className, style)}>
			<path d='M 10 2 L 14 2 Q 16 2 16 4 L 16 20 Q 16 22 14 22 L 10 22 Q 8 22 8 20 L 8 4 Q 8 2 10 2 Z' />
			<circle cx='12' cy='5.5' r='1' />
			<line x1='9.5' y1='10' x2='14.5' y2='10' />
			<line x1='9.5' y1='12.5' x2='14.5' y2='12.5' />
			<line x1='9.5' y1='15' x2='14.5' y2='15' />
		</svg>
	);
}

// ─── Custom SVG: Access Control Panel (ACP / controller) ────────────────────
// Landscape enclosure · PCB board · 3 status LEDs · CPU chip · terminal pins

function AccessControlPanel({ size = 24, color = 'currentColor', strokeWidth = 1.5, className, style }) {
	return (
		<svg {...svgProps(size, color, strokeWidth, className, style)}>
			<path d='M 3 3 L 21 3 Q 23 3 23 5 L 23 19 Q 23 21 21 21 L 3 21 Q 1 21 1 19 L 1 5 Q 1 3 3 3 Z' />
			<path d='M 4 5.5 L 20 5.5 Q 21 5.5 21 6.5 L 21 15.5 Q 21 16.5 20 16.5 L 4 16.5 Q 3 16.5 3 15.5 L 3 6.5 Q 3 5.5 4 5.5 Z' />
			<circle cx='7' cy='9' r='1.25' />
			<circle cx='12' cy='9' r='1.25' />
			<circle cx='17' cy='9' r='1.25' />
			<path d='M 9.5 11.5 L 14.5 11.5 Q 15 11.5 15 12 L 15 15 Q 15 15.5 14.5 15.5 L 9.5 15.5 Q 9 15.5 9 15 L 9 12 Q 9 11.5 9.5 11.5 Z' />
			<line x1='5.5' y1='18.5' x2='5.5' y2='21' />
			<line x1='9' y1='18.5' x2='9' y2='21' />
			<line x1='12.5' y1='18.5' x2='12.5' y2='21' />
			<line x1='16' y1='18.5' x2='16' y2='21' />
		</svg>
	);
}

// ─── Custom SVG: Digital Lock (smart lock with keypad) ───────────────────────
// Portrait panel · 3×3 filled keypad dots · hub + lever on the right

function DigitalLock({ size = 24, color = 'currentColor', strokeWidth = 1.5, className, style }) {
	return (
		<svg {...svgProps(size, color, strokeWidth, className, style)}>
			<path d='M 3.5 1 L 9.5 1 Q 12 1 12 3.5 L 12 20.5 Q 12 23 9.5 23 L 3.5 23 Q 1 23 1 20.5 L 1 3.5 Q 1 1 3.5 1 Z' />
			<circle cx='6.5' cy='16' r='2' />
			<path d='M 8.25 14.25 L 20.25 14.25 Q 22 14.25 22 16 Q 22 17.75 20.25 17.75 L 8.25 17.75 Q 6.5 17.75 6.5 16 Q 6.5 14.25 8.25 14.25 Z' />
			{[5.0, 7.5, 10.0].flatMap((y) =>
				[4.0, 6.5, 9.0].map((x, i) => (
					<circle key={`${y}-${i}`} cx={x} cy={y} r='0.65' fill={color} stroke='none' />
				))
			)}
		</svg>
	);
}

// ─── Custom SVG: Gateway (BLE/IoT hub) ──────────────────────────────────────
// Box + 2 signal arcs + LED dot

function Gateway({ size = 24, color = 'currentColor', strokeWidth = 1.5, className, style }) {
	return (
		<svg {...svgProps(size, color, strokeWidth, className, style)}>
			<path d='M 5 16 L 19 16 Q 21 16 21 18 L 21 20 Q 21 22 19 22 L 5 22 Q 3 22 3 20 L 3 18 Q 3 16 5 16 Z' />
			<path d='M 8.5 16 C 8.5 14.067 10.067 12.5 12 12.5 C 13.933 12.5 15.5 14.067 15.5 16' />
			<path d='M 5.5 16 C 5.5 12.41 8.41 9.5 12 9.5 C 15.59 9.5 18.5 12.41 18.5 16' />
			<circle cx='16.5' cy='19' r='1' />
		</svg>
	);
}

// ─── Lucide adapter ──────────────────────────────────────────────────────────

function l(Icon) {
	return function LucideIconWrapper({ size = 24, color = 'currentColor', strokeWidth = 1.5, className, style }) {
		return <Icon size={size} color={color} strokeWidth={strokeWidth} className={className} style={style} />;
	};
}

// ─── Icon Registry ───────────────────────────────────────────────────────────

export const ICON_REGISTRY = {
	// Hardware
	'door': l(DoorClosed),
	'door-open': l(DoorOpen),
	'lock': l(Lock),
	'lock-open': l(LockOpen),
	'camera': DomeCamera,
	'dome-camera': DomeCamera,
	'bullet-camera': BulletCamera,
	'reader': l(ScanLine),
	'wiegand-reader': WiegandReader,
	'access-panel': AccessControlPanel,
	'card': l(CreditCard),
	'intercom': l(PhoneCall),
	'sensor': l(Radio),
	'signal': l(Radio),
	'online': l(Wifi),
	'offline': l(WifiOff),
	'boom-gate': BoomGate,
	'locker-unit': LockerUnit,
	// Short alias for the DeviceBadge ICON_FOR mapping. `locker-unit`
	// is the canonical hardware key; `locker` matches the DeviceType
	// JSDoc union exposed by DeviceBadge.
	'locker': LockerUnit,
	'garage': GarageDoor,
	'turnstile': TurnstileIcon,
	'lift': LiftElevator,
	'gateway': Gateway,
	'digital-lock': DigitalLock,

	// Spaces & Buildings
	'building': l(Building2),
	'floor': l(Layers),
	'zone': l(BoxSelect),
	'room': l(LayoutDashboard),
	'space': l(LayoutGrid),
	'map': l(Map),
	'location': l(MapPin),
	'asset': l(Package),

	// People & Credentials
	'user': l(User),
	'users': l(Users),
	'visitor': l(UserPlus),
	// `user-plus` is a literal-name alias for the Actions Strip registry
	// (Invite people action). `visitor` is the semantic role-based alias
	// used by visitor flows; both point at the same Lucide glyph.
	'user-plus': l(UserPlus),
	'verified-user': l(UserCheck),
	'user-x': l(UserX),
	'credential': l(KeyRound),
	'mobile-credential': l(Smartphone),
	'fingerprint': l(Fingerprint),
	'pin': l(Hash),
	'face': l(ScanFace),
	'qr-code': l(QrCode),

	// Products
	'product-access': l(ShieldCheck),
	'product-rooms': l(Hotel),
	'product-spaces': l(LayoutGrid),
	'product-vision': l(Eye),
	'product-pass': l(Ticket),
	'product-lockers': LockerUnit,
	'ai': l(BrainCircuit),

	// Status & Actions
	'granted': l(CheckCircle2),
	'denied': l(XCircle),
	'shield-check': l(ShieldCheck),
	'shield-x': l(ShieldX),
	'schedule': l(Clock),
	'calendar': l(Calendar),
	'alert': l(AlertTriangle),
	'activity': l(Activity),
	'scroll-text': l(ScrollText),
	'notebook-pen': l(NotebookPen),
	// A generated REPORT — a document with a chart in it. Deliberately not `scroll-text`, which this
	// app already spends on "Activity" (every V2 drawer's activity tab, and the audit entity), nor
	// `notebook-pen`, which is "Details" in those same drawers. One glyph, one meaning.
	'report': l(FileChartColumn),
	// The audit trail: a record of changes over time. Not `scroll-text`, which is "Activity" in every drawer.
	'audit-log': l(FileClock),
	'user-round-plus': l(UserRoundPlus),
	'settings': l(Settings),
	'notification': l(Bell),
	'battery': l(BatteryMedium),
	'battery-low': l(BatteryLow),
	'battery-warning': l(BatteryWarning),
	'battery-medium': l(BatteryMedium),
	'battery-full': l(BatteryFull),
	'battery-empty': l(Battery),
	'plug': l(Plug),
	'live': l(Zap),
	'bolt': l(Zap),
	'infinity': l(InfinityIcon),
	'global': l(Globe),
	'globe': l(Globe),
	'wrench': l(Wrench),
	'code': l(Code),
	'eye': l(Eye),
	'trash': l(Trash2),
	'edit': l(Pencil),

	// Media & navigation. ⚠️ An unregistered name renders NOTHING (ZezamiiIcon returns null), and an
	// icon-only AntD Button then paints a correctly-sized blank square — a control that looks present
	// to a test querying aria-label and is invisible to the person using it. Device install photos
	// needed all four.
	'image': l(ImageIcon),
	'image-plus': l(ImagePlus),
	'replace': l(RefreshCw),
	'chevron-left': l(ChevronLeft),
	'chevron-right': l(ChevronRight)
};

// ─── Public component ────────────────────────────────────────────────────────

export function ZezamiiIcon({ name, size = 24, color = 'currentColor', strokeWidth = 1.5, className, style }) {
	const IconComp = ICON_REGISTRY[name];
	if (!IconComp) {
		if (process.env.NODE_ENV === 'development') {
			console.warn(`[ZezamiiIcon] Unknown icon name: "${name}"`);
		}
		return null;
	}
	return <IconComp size={size} color={color} strokeWidth={strokeWidth} className={className} style={style} />;
}

export default ZezamiiIcon;
